import { z } from 'zod';

import { IdSchema, IsoDateSchema } from './common.js';
import { TrackingModesSchema } from './tracker.js';

// ─── Enum schemas ─────────────────────────────────────────────────────────

export const GoalTypeSchema = z.enum(['lose_weight', 'gain_muscle', 'bulk', 'maintain', 'other']);
export type GoalType = z.infer<typeof GoalTypeSchema>;

export const SubGoalSchema = z.enum([
  'abs',
  'belly_fat',
  'stamina',
  'flexibility',
  'athletic',
  'postpartum',
  'general_health',
]);
export type SubGoal = z.infer<typeof SubGoalSchema>;

export const GenderSchema = z.enum(['male', 'female', 'prefer_not_to_say']);
export type Gender = z.infer<typeof GenderSchema>;

export const HealthContextSchema = z.enum([
  'none',
  'pregnant',
  'postpartum',
  'medical_supervision',
]);
export type HealthContext = z.infer<typeof HealthContextSchema>;

export const BodyTypeSchema = z.enum(['ectomorph', 'mesomorph', 'endomorph']);
export type BodyType = z.infer<typeof BodyTypeSchema>;

export const FoodPreferenceSchema = z.enum([
  'non_vegetarian',
  'vegetarian',
  'vegan',
  'eggetarian',
  'pescetarian',
]);
export type FoodPreference = z.infer<typeof FoodPreferenceSchema>;

export const EnvironmentSchema = z.enum(['gym', 'home', 'outdoor']);
export type Environment = z.infer<typeof EnvironmentSchema>;

export const ExperienceLevelSchema = z.enum(['beginner', 'intermediate', 'advanced']);
export type ExperienceLevel = z.infer<typeof ExperienceLevelSchema>;

export const SessionDurationSchema = z.enum(['under_30', '30_45', '45_60', '60_90', '90_plus']);
export type SessionDuration = z.infer<typeof SessionDurationSchema>;

// Named OnboardingEquipmentSchema (not EquipmentSchema) to avoid colliding
// with exercise.ts's EquipmentSchema, which models per-exercise equipment
// with a narrower value set.
export const OnboardingEquipmentSchema = z.enum([
  'barbell',
  'dumbbell',
  'machine',
  'cable',
  'bodyweight',
  'resistance_bands',
  'kettlebell',
  'other',
]);
export type OnboardingEquipment = z.infer<typeof OnboardingEquipmentSchema>;

export const OnboardingStepSchema = z.enum(['goal', 'body_stats', 'activity', 'training']);
export type OnboardingStep = z.infer<typeof OnboardingStepSchema>;

export const GoalRealismClassificationSchema = z.enum(['realistic', 'aggressive', 'unrealistic']);
export type GoalRealismClassification = z.infer<typeof GoalRealismClassificationSchema>;

// ─── Nested schemas (onboardingData JSON blob) ────────────────────────────

export const SportEntrySchema = z.object({
  name: z.string().min(1).max(80),
  sessions_per_week: z.number().int().min(1).max(7),
  hours_per_session: z.number().min(0.25).max(12),
  intensity: z.enum(['light', 'moderate', 'vigorous']),
});
export type SportEntry = z.infer<typeof SportEntrySchema>;

export const LifestyleBaselineSchema = z.object({
  daily_movement: z.enum(['mostly_seated', 'mixed', 'mostly_on_feet']).optional(),
  sedentary_hours: z.number().min(0).max(24).optional(),
  light_activity_hours: z.number().min(0).max(24).optional(),
  sleep_hours: z.number().min(0).max(14).optional(),
});
export type LifestyleBaseline = z.infer<typeof LifestyleBaselineSchema>;

export const OnboardingDataSchema = z.object({
  sports: z.array(SportEntrySchema).default([]),
  lifestyleBaseline: LifestyleBaselineSchema.optional(),
  healthContext: HealthContextSchema.optional(),
  computedActivityMultiplier: z.number().min(1).max(3).optional(),
});
export type OnboardingData = z.infer<typeof OnboardingDataSchema>;

// ─── Main schemas ─────────────────────────────────────────────────────────

// Hoisted from section (d) below: UserProfileAISchema's onboardingSummary
// field references OnboardingSummarySchema, so it must be declared first.
export const OnboardingSummarySchema = z.object({
  mission_statement: z.string().min(1).max(2000),
  plan_overview: z.string().min(1).max(2000),
  first_week_focus: z.string().min(1).max(2000),
  realistic_milestone: z.string().min(1).max(2000),
});
export type OnboardingSummary = z.infer<typeof OnboardingSummarySchema>;

export const UserProfileAISchema = z.object({
  userId: IdSchema,
  trackingModes: TrackingModesSchema.default(['nutrition', 'training']),
  goalType: GoalTypeSchema.nullable(),
  subGoals: z.array(SubGoalSchema).default([]),
  currentWeightKg: z.number().positive().nullable(),
  targetWeightKg: z.number().nullable(),
  targetDate: IsoDateSchema.nullable(),
  age: z.number().int().min(16).nullable(),
  heightCm: z.number().min(40).max(250).nullable(),
  gender: GenderSchema.nullable(),
  bodyType: BodyTypeSchema.nullable(),
  foodPreference: FoodPreferenceSchema.nullable(),
  environments: z.array(EnvironmentSchema).default([]),
  trainingSkipped: z.boolean().default(false),
  experienceLevel: ExperienceLevelSchema.nullable(),
  daysPerWeek: z.number().int().nullable(),
  sessionDuration: SessionDurationSchema.nullable(),
  equipment: z.array(OnboardingEquipmentSchema).default([]),
  limitations: z.array(z.string().max(200)).default([]),
  onboardingData: OnboardingDataSchema,
  onboardingSummary: OnboardingSummarySchema.nullable(),
  completedSteps: z.array(OnboardingStepSchema).default([]),
  completedAt: IsoDateSchema.nullable(),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
});
export type UserProfileAI = z.infer<typeof UserProfileAISchema>;
export const OnboardingProfileEnvelopeSchema = z.object({
  profile: UserProfileAISchema.nullable(),
});
export type OnboardingProfileEnvelope = z.infer<typeof OnboardingProfileEnvelopeSchema>;

export const UpdateGoalSchema = z
  .object({
    goalType: GoalTypeSchema,
    subGoals: z.array(SubGoalSchema).default([]),
    currentWeightKg: z.number().positive(),
    targetWeightKg: z.number().positive().nullable().optional(),
    targetDate: IsoDateSchema,
  })
  .superRefine((value, ctx) => {
    if (
      value.goalType === 'lose_weight' &&
      value.targetWeightKg != null &&
      value.targetWeightKg >= value.currentWeightKg
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetWeightKg'],
        message: 'Weight-loss target must be below current weight',
      });
    }
    if (
      (value.goalType === 'gain_muscle' || value.goalType === 'bulk') &&
      value.targetWeightKg != null &&
      value.targetWeightKg <= value.currentWeightKg
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetWeightKg'],
        message: 'Weight-gain target must be above current weight',
      });
    }
    if (
      value.goalType === 'maintain' &&
      value.targetWeightKg != null &&
      Math.abs(value.targetWeightKg - value.currentWeightKg) > 0.5
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetWeightKg'],
        message: 'Maintenance target must stay within 0.5 kg of current weight',
      });
    }
    if (new Date(value.targetDate).getTime() <= Date.now()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetDate'],
        message: 'Target date must be in the future',
      });
    }
  });
export type UpdateGoalInput = z.infer<typeof UpdateGoalSchema>;

export const UpdateBodyStatsSchema = z.object({
  age: z.number().int().min(16),
  heightCm: z.number().min(40).max(250),
  gender: GenderSchema,
  healthContext: HealthContextSchema,
  foodPreference: FoodPreferenceSchema.optional(),
});
export type UpdateBodyStatsInput = z.infer<typeof UpdateBodyStatsSchema>;

export const UpdateActivityProfileSchema = z.object({
  environments: z.array(EnvironmentSchema).default([]),
  sports: z.array(SportEntrySchema).default([]),
  lifestyleBaseline: LifestyleBaselineSchema.optional(),
});
export type UpdateActivityProfileInput = z.infer<typeof UpdateActivityProfileSchema>;

export const UpdateTrainingPreferenceSchema = z
  .object({
    trainingSkipped: z.boolean().default(false),
    experienceLevel: ExperienceLevelSchema.optional(),
    daysPerWeek: z.number().int().min(1).max(7).optional(),
    sessionDuration: SessionDurationSchema.optional(),
    equipment: z.array(OnboardingEquipmentSchema).default([]),
    limitations: z.array(z.string().max(200)).default([]),
  })
  .superRefine((value, ctx) => {
    if (value.trainingSkipped) return;
    for (const field of ['experienceLevel', 'daysPerWeek', 'sessionDuration'] as const) {
      if (value[field] == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: `${field} is required when structured training is enabled`,
        });
      }
    }
  });
export type UpdateTrainingPreferenceInput = z.infer<typeof UpdateTrainingPreferenceSchema>;

// ─── AI output schemas ────────────────────────────────────────────────────
// (OnboardingSummarySchema is hoisted above with the main schemas.)

export const TrainingDaysSuggestionSchema = z.object({
  recommended_days: z.number().int().min(1).max(7),
  reasoning: z.string().min(1).max(2000),
  min_days: z.number().int().min(1).max(7),
  max_days: z.number().int().min(1).max(7),
});
export type TrainingDaysSuggestion = z.infer<typeof TrainingDaysSuggestionSchema>;

export const GoalRealismCheckRequestSchema = z.object({
  goalType: GoalTypeSchema,
  currentWeightKg: z.number(),
  targetWeightKg: z.number().nullable().optional(),
  targetDate: IsoDateSchema,
});
export type GoalRealismCheckRequest = z.infer<typeof GoalRealismCheckRequestSchema>;

export const GoalRealismCheckResponseSchema = z.object({
  classification: GoalRealismClassificationSchema,
  message: z.string().min(1).max(2000),
  suggestedDate: IsoDateSchema.nullable().optional(),
  weeklyChangePct: z.number(),
});
export type GoalRealismCheckResponse = z.infer<typeof GoalRealismCheckResponseSchema>;
